import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Container, FaqItem, SectionLabel } from '../../design';
import './product-shared.css';
/** Product page hero: section label, title, lead and the UI close-up beside (or below) it. */ export function ProductHero({ k, label, title, lead, stack, align = 'center', watermark, actions, copyStyle, leadStyle, children }) {
    const inner = [
        'mk-pp-hero__inner'
    ];
    if (stack) inner.push('mk-pp-hero__inner--stack');
    if (align === 'start') inner.push('mk-pp-hero__inner--start');
    return /*#__PURE__*/ _jsxs("section", {
        className: watermark ? 'mk-pp-hero mk-pp-hero--marked' : 'mk-pp-hero',
        children: [
            watermark && /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                className: "mk-pp-hero__watermark",
                children: watermark
            }),
            /*#__PURE__*/ _jsxs(Container, {
                className: inner.join(' '),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: stack ? 'mk-pp-hero__copy mk-pp-hero__copy--narrow' : 'mk-pp-hero__copy',
                        style: copyStyle,
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: k,
                                children: label
                            }),
                            /*#__PURE__*/ _jsx("h1", {
                                className: "mk-pp-hero__title",
                                children: title
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-pp-hero__lead",
                                style: leadStyle,
                                children: lead
                            }),
                            actions && /*#__PURE__*/ _jsx("div", {
                                className: "mk-pp-hero__actions",
                                children: actions
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: stack ? 'rhd-invert' : 'mk-pp-hero__mock rhd-invert',
                        children: children
                    })
                ]
            })
        ]
    });
}
/** "How it works" style section: 手順 label (text overridable) and a title. */ export function ProductSection({ id, label = 'How it works', title, wide, loose, children }) {
    const cls = [
        'mk-pp-section__title'
    ];
    if (wide || loose) cls.push('mk-pp-section__title--loose');
    if (wide) cls.push('mk-pp-section__title--wide');
    return /*#__PURE__*/ _jsx("section", {
        id: id,
        className: "mk-ground--sora",
        children: /*#__PURE__*/ _jsxs(Container, {
            className: "mk-pp-section",
            children: [
                /*#__PURE__*/ _jsx(SectionLabel, {
                    k: "手順",
                    children: label
                }),
                /*#__PURE__*/ _jsx("h2", {
                    className: cls.join(' '),
                    children: title
                }),
                children
            ]
        })
    });
}
/** Vertical list of numbered steps inside a ProductSection. */ export function ProductSteps({ children }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "mk-pp-steps",
        children: children
    });
}
/** One numbered step: kanji numeral, title and text, with the page's close-up beside it.
    The close-up sizes itself; `mk-pp-step__ui` (+ `--framed`) gives the usual box. */ export function ProductStep({ k, title, text, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "mk-pp-step",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mk-pp-step__copy",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "mk-pp-step__k",
                        children: k
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "mk-pp-step__title",
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mk-pp-step__text",
                        children: text
                    })
                ]
            }),
            children
        ]
    });
}
/** Paper-2 band with a smaller title: "Details that matter". Pass `items` for the
    standard ruled grid, or `children` for a page-specific body. */ export function ProductDetails({ title, items, children, tightHeadings }) {
    return /*#__PURE__*/ _jsx("section", {
        className: tightHeadings ? 'mk-pp-details mk-pp-details--tight mk-ground--kinari' : 'mk-pp-details mk-ground--kinari',
        children: /*#__PURE__*/ _jsxs(Container, {
            className: "mk-pp-details__inner",
            children: [
                /*#__PURE__*/ _jsx("h2", {
                    className: "mk-pp-details__title",
                    children: title
                }),
                items ? /*#__PURE__*/ _jsx("div", {
                    className: "mk-pp-details__grid",
                    children: items.map(([b, p], i)=>/*#__PURE__*/ _jsxs("div", {
                            className: "mk-pp-details__item",
                            children: [
                                /*#__PURE__*/ _jsx("b", {
                                    children: b
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: p
                                })
                            ]
                        }, i))
                }) : children
            ]
        })
    });
}
/** Product-page FAQ (larger than the site FAQ). */ export function ProductFaq({ title, items, tightTop }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: tightTop ? 'mk-pp-faq mk-pp-faq--tight-top' : 'mk-pp-faq',
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "mk-pp-faq__title",
                children: title
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-pp-faq__list",
                children: items.map(([q, a], i)=>/*#__PURE__*/ _jsx(FaqItem, {
                        size: "lg",
                        q: q,
                        children: a
                    }, i))
            })
        ]
    });
}
/** Link card to the next page in the product tour. */ export function NextLink({ label, title, go, href }) {
    return /*#__PURE__*/ _jsx("section", {
        children: /*#__PURE__*/ _jsx(Container, {
            className: "mk-pp-next-wrap",
            children: /*#__PURE__*/ _jsxs("a", {
                href: href,
                className: "mk-pp-next",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "mk-pp-next__text",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-caps",
                                children: label
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "mk-pp-next__title",
                                children: title
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "mk-pp-next__go",
                        children: go
                    })
                ]
            })
        })
    });
}
